import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// iOS-style edge swipe back: the current screen follows the finger and slides away.
// Su iOS NATIVO (Capacitor) è disattivato: lo swipe è gestito dal WKWebView
// (allowsBackForwardNavigationGestures in AppDelegate.swift) con animazione di sistema.
const isNativeIos = () => {
  try {
    const c = window.Capacitor;
    return !!c?.isNativePlatform?.() && c?.getPlatform?.() === "ios";
  } catch { return false; }
};

const ROOT_PATHS = new Set(["/", "/login", "/register", "/app", "/app/home", "/app/here", "/app/alerts", "/app/profile", "/admin"]);
const EDGE = 44;        // px from the left edge to start tracking (generous for iOS)
const THRESHOLD = 70;   // px horizontal distance to trigger back
const MAX_DRIFT = 70;   // px max vertical drift before cancelling

export const EdgeSwipeBack = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  useEffect(() => {
    if (isNativeIos()) return; // gesture nativa WKWebView attiva: niente doppio back
    let startX = null, startY = null, tracking = false, committed = false, animating = false;

    const getEl = () => document.querySelector(".app-container");

    const clearStyles = (el) => {
      if (!el) return;
      el.style.transition = "";
      el.style.transform = "";
      el.style.willChange = "";
      el.style.boxShadow = "";
    };

    const cancelDrag = () => {
      const el = getEl();
      if (el && committed) {
        el.style.transition = "transform 0.2s ease-out";
        el.style.transform = "translateX(0px)";
        setTimeout(() => clearStyles(el), 220);
      }
      tracking = false;
      committed = false;
      startX = startY = null;
    };

    // esclusi: contesti con gesture proprie (mappa, story viewer/studio, editor, zoom gemma, vetrina, compositore testo)
    const EXCLUDE = ".leaflet-container, .story-viewer, .sst-root, .stc-root, .gz-root, .lvv-root, [data-testid='media-editor'], [data-testid='image-cropper-dialog'], input, textarea, select, [role='slider'], video, [data-noswipe]";
    const onStart = (e) => {
      if (animating || ROOT_PATHS.has(pathRef.current)) return;
      if (e.target && e.target.closest && e.target.closest(EXCLUDE)) return;
      const t = e.touches[0];
      if (t.clientX <= EDGE) {
        startX = t.clientX;
        startY = t.clientY;
        tracking = true;
        committed = false;
      }
    };

    const onMove = (e) => {
      if (!tracking || animating) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      if (!committed) {
        if (dy > MAX_DRIFT || (dy > 20 && dy > dx)) { tracking = false; return; }
        if (dx > 12 && dx > dy) {
          committed = true;
          const el = getEl();
          if (el) {
            el.style.willChange = "transform";
            el.style.transition = "none";
          }
        }
      }
      if (committed && dx >= 0) {
        const el = getEl();
        if (el) {
          el.style.transform = `translateX(${dx}px)`;
          el.style.boxShadow = "-12px 0 28px rgba(0,0,0,0.35)";
        }
      }
    };

    const onEnd = (e) => {
      if (!tracking) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      const el = getEl();
      const shouldGoBack = committed && dx > THRESHOLD && dy <= MAX_DRIFT;
      tracking = false;
      if (!committed || !el) { committed = false; return; }
      if (shouldGoBack) {
        animating = true;
        el.style.transition = "transform 0.18s ease-out";
        el.style.transform = "translateX(100vw)";
        setTimeout(() => {
          if (window.history.length > 1) navigate(-1);
          else navigate("/app");
          requestAnimationFrame(() => {
            clearStyles(el);
            animating = false;
          });
        }, 170);
      } else {
        cancelDrag();
      }
      committed = false;
    };

    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    window.addEventListener("touchcancel", cancelDrag, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", cancelDrag);
      clearStyles(getEl());
    };
  }, [navigate]);

  return null;
};
